// Pure helpers for the Invoices tab (CHO-761): sorting, overdue derivation,
// unit-option plumbing. Kept out of the components so those modules export
// only components (react-refresh), mirroring lib/payments.ts (CHO-754).

import type { InvoiceListItem, InvoicePaymentStatus } from '../types/invoices';

export interface UnitOption {
  /** Stable key — 'unassigned' for null-property invoices, else `p:<propertyId>`. */
  key: string;
  label: string;
}

export const ALL_STATUSES: InvoicePaymentStatus[] = ['unpaid', 'paid', 'failed', 'unknown'];

/** Local calendar date as 'YYYY-MM-DD' — due dates are stored the same way. */
export function localDateISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * A row is overdue when it is still unpaid or a failed (declined, retryable)
 * attempt and its due date is strictly before today, local time. Excluded
 * rows never count — they are operator-marked and out of the money picture.
 */
export function isOverdue(invoice: InvoiceListItem, todayISO: string): boolean {
  if (invoice.excluded) return false;
  if (invoice.paymentStatus !== 'unpaid' && invoice.paymentStatus !== 'failed') return false;
  if (!invoice.dueDate) return false;
  return invoice.dueDate.slice(0, 10) < todayISO;
}

function compareRows(a: InvoiceListItem, b: InvoiceListItem): number {
  // Secondary order: invoice date descending (newest first), then id for stability.
  const byInvoiceDate = b.invoiceDate.localeCompare(a.invoiceDate);
  if (byInvoiceDate !== 0) return byInvoiceDate;
  return b.id - a.id;
}

/**
 * Default order for the Invoices tab: due date ascending (soonest first).
 * Rows without a due date never interleave with dated rows — they are
 * returned separately so the table can render them after all dated rows
 * under an explicit "No due date" divider.
 */
export function sortInvoices(invoices: InvoiceListItem[]): { dated: InvoiceListItem[]; undated: InvoiceListItem[] } {
  const dated = invoices.filter((i) => i.dueDate !== null).sort((a, b) => {
    const byDue = a.dueDate!.localeCompare(b.dueDate!);
    return byDue !== 0 ? byDue : compareRows(a, b);
  });
  const undated = invoices.filter((i) => i.dueDate === null).sort(compareRows);
  return { dated, undated };
}

/** Property column fallback: invoices without a property group under Unassigned. */
export function propertyLabel(invoice: Pick<InvoiceListItem, 'propertyId' | 'propertyName'>): string {
  if (invoice.propertyId === null) return 'Unassigned';
  return invoice.propertyName ?? `Property ${invoice.propertyId}`;
}
